import { Capacitor, PluginListenerHandle } from "@capacitor/core";
import {
  RevenueCatUI,
  PAYWALL_RESULT,
  IOS_PAYWALL_PRESENTATION_STYLE,
  ANDROID_PAYWALL_PRESENTATION_STYLE,
} from "@revenuecat/purchases-capacitor-ui";
import {
  Purchases,
  PurchasesOffering,
  LOG_LEVEL,
} from "@revenuecat/purchases-capacitor";
import type { WebPurchaseRedemptionResult } from "@revenuecat/purchases-capacitor";
import { isProduction, logException } from "@um/ui";
import type { Account, WithRef } from "@um/contracts";
import { AppsFlyer } from "appsflyer-capacitor-plugin";
import { FirebaseAnalytics } from "@capacitor-firebase/analytics";
import { sendConversionEvent } from "@/services/social";
import {
  getRevenueCatPublicApiKey,
  type OfferingMetadata,
  type PaywallVariables,
  type SubscriberAttributes,
  type AccountSessionContext,
  type PaywallPresentationRequest,
  type ResolvedExperimentOffering,
  type RevenueCatPlatform,
  type SubscriptionSnapshot,
  dateFromNativeRevenueCatString,
  deriveLifecycleStatus,
  entitlementDisplayName,
  normalizePurchaseStore,
  resolveOfferingForPlacementPath,
  SUBSCRIPTION_ENTITLEMENTS,
  subscriberAttributesFromAccount,
} from "@um/ui/services/revenueCat";

/** RevenueCat redemption-link URL scheme (see iOS Info.plist / Android manifest). */
export const REVENUECAT_WEB_REDEMPTION_URL_SCHEME = "rc-b176b5aeb4";

let paywallListenerHandles: PluginListenerHandle[] = [];
let nativePurchasesConfigured = false;

export function isRevenueCatWebRedemptionUrl(urlString: string): boolean {
  return urlString.startsWith(`${REVENUECAT_WEB_REDEMPTION_URL_SCHEME}://`);
}

/**
 * Parses a RevenueCat web-billing redemption deep link and redeems it when valid.
 * Returns null when the URL is not a redemption link.
 */
export async function redeemWebPurchaseFromUrl(
  urlString: string
): Promise<WebPurchaseRedemptionResult | null> {
  let parsed: Awaited<
    ReturnType<typeof Purchases.parseAsWebPurchaseRedemption>
  > | null = null;
  try {
    parsed = await Purchases.parseAsWebPurchaseRedemption({ urlString });
  } catch (e) {
    logException(e);
    return null;
  }
  const webPurchaseRedemption = parsed?.webPurchaseRedemption;
  if (!webPurchaseRedemption) {
    return null;
  }
  return Purchases.redeemWebPurchase({ webPurchaseRedemption });
}

function detachPaywallListeners(): void {
  paywallListenerHandles.forEach((h) => void h.remove());
  paywallListenerHandles = [];
}

/** True when the user should be treated as having passed the paywall flow. */
function paywallResultIndicatesAccess(result: PAYWALL_RESULT): boolean {
  return (
    result === PAYWALL_RESULT.PURCHASED ||
    result === PAYWALL_RESULT.RESTORED ||
    result === PAYWALL_RESULT.NOT_PRESENTED
  );
}

/**
 * SKAN conversion values can only encode events the AppsFlyer SDK observes on-device;
 * the server-side RevenueCat → AppsFlyer integration (rc_trial_started_event) is
 * invisible to SKAN, so iOS campaigns would otherwise only see installs.
 */
/**
 * Funnel instrumentation: report actual paywall exposure to AppsFlyer/GA.
 * Fired from the presentation result (not the resolve step) so NOT_PRESENTED
 * — user already entitled per RevenueCat — never counts as a paywall view.
 */
function reportPaywallEvent(
  event: "um_paywall_shown" | "um_paywall_dismissed",
  details: { placement?: string; offeringId?: string; dismissCount?: number }
): void {
  void sendConversionEvent(event, {
    placement: details.placement || "unknown",
    offering: details.offeringId || "default",
    dismissCount: details.dismissCount ?? 0,
  });
}

async function reportPurchaseConversionEvent(): Promise<void> {
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    const startedTrial = Object.values(
      customerInfo.subscriptionsByProductIdentifier
    ).some((sub) => sub.isActive && sub.periodType === "TRIAL");
    await sendConversionEvent(startedTrial ? "um_start_trial" : "um_subscribe");
  } catch (e) {
    console.error("Error reporting purchase conversion event", e);
    logException(e);
  }
}

export function createNativeRevenueCatPlatform(): RevenueCatPlatform {
  return {
    isReady: () => nativePurchasesConfigured,

    async syncAccountInEffect(
      account: WithRef<Account>,
      ctx: AccountSessionContext
    ): Promise<void> {
      const {
        configuredAccountId,
        setConfiguredAccountId,
        triggerSubscriptionRefresh,
        setRevenueCatAccountSynced,
      } = ctx;

      if (!configuredAccountId) {
        try {
          await configureNativePurchases(account);
          setConfiguredAccountId(account.ref.id);
          // The account-creation promo grant is written by the backend around
          // the same time configure's first customerInfo fetch runs. Drop that
          // snapshot so the initial refresh reads post-grant state — otherwise
          // the SDK serves the pre-grant "no entitlements" cache for ~5
          // minutes, hiding the promo from paywall gates and the free-access
          // banner.
          await Purchases.invalidateCustomerInfoCache();
          triggerSubscriptionRefresh();
          setRevenueCatAccountSynced(true);
        } catch (e) {
          logException(e);
          setRevenueCatAccountSynced(false);
        }
      } else if (configuredAccountId !== account.ref.id) {
        console.log(
          "[payments] Logging in to RevenueCat with user ID",
          account.ref.id
        );
        setRevenueCatAccountSynced(false);
        try {
          console.log("[paywall] logIn", account.ref.id);
          await Purchases.logIn({
            appUserID: account.ref.id,
          });
          console.log("[paywall] invalidateCustomerInfoCache");
          await Purchases.syncAttributesAndOfferingsIfNeeded();

          setConfiguredAccountId(account.ref.id);
          // Same promo-grant race as the configure path: the switched-to
          // account may have been created (and granted) moments ago.
          await Purchases.invalidateCustomerInfoCache();
          triggerSubscriptionRefresh();
          setRevenueCatAccountSynced(true);
        } catch (e) {
          logException(e);
          setRevenueCatAccountSynced(false);
        }
      } else {
        // Already configured for this account (e.g. auth briefly cleared isReady).
        setRevenueCatAccountSynced(true);
      }
    },

    async fetchLatestSubscriptionSnapshot(): Promise<SubscriptionSnapshot[]> {
      const { customerInfo } = await Purchases.getCustomerInfo();
      const nowMs = Date.now();
      const subs = customerInfo.subscriptionsByProductIdentifier;
      const snapshots: SubscriptionSnapshot[] = [];

      for (const entitlementId of SUBSCRIPTION_ENTITLEMENTS) {
        const entitlement =
          customerInfo.entitlements.active[entitlementId] ??
          customerInfo.entitlements.all[entitlementId];
        if (!entitlement) continue;

        const productIdentifier = entitlement.productIdentifier || "";
        // The subscription row carries the richer billing fields (grace period,
        // refund, billing issues) that the entitlement object doesn't expose.
        const info = productIdentifier ? subs[productIdentifier] : undefined;

        const isActive = info?.isActive ?? entitlement.isActive;
        const willRenew = info?.willRenew ?? entitlement.willRenew;
        const expiresDate =
          dateFromNativeRevenueCatString(info?.expiresDate) ??
          dateFromNativeRevenueCatString(entitlement.expirationDate) ??
          (productIdentifier
            ? dateFromNativeRevenueCatString(
                customerInfo.allExpirationDates[productIdentifier]
              )
            : null);
        const purchaseDate =
          dateFromNativeRevenueCatString(info?.purchaseDate) ??
          dateFromNativeRevenueCatString(entitlement.latestPurchaseDate) ??
          new Date(0);
        const periodType = info?.periodType ?? entitlement.periodType ?? "";
        const store = info?.store ?? entitlement.store ?? null;
        const unsubscribeDetectedAt =
          dateFromNativeRevenueCatString(info?.unsubscribeDetectedAt) ??
          dateFromNativeRevenueCatString(entitlement.unsubscribeDetectedAt);
        const billingIssuesDetectedAt =
          dateFromNativeRevenueCatString(info?.billingIssuesDetectedAt) ??
          dateFromNativeRevenueCatString(entitlement.billingIssueDetectedAt);
        const gracePeriodExpiresDate = dateFromNativeRevenueCatString(
          info?.gracePeriodExpiresDate
        );
        const refundedAt = dateFromNativeRevenueCatString(info?.refundedAt);

        const forLifecycle = {
          isActive,
          willRenew,
          refundedAt,
          billingIssuesDetectedAt,
          gracePeriodExpiresDate,
        };

        snapshots.push({
          entitlementId,
          productIdentifier: productIdentifier || entitlementId,
          displayName: entitlementDisplayName(entitlementId),
          purchaseStore: normalizePurchaseStore(store ?? null),
          isActive,
          willRenew,
          expiresDate,
          purchaseDate,
          periodType,
          unsubscribeDetectedAt,
          billingIssuesDetectedAt,
          gracePeriodExpiresDate,
          refundedAt,
          lifecycleStatus: deriveLifecycleStatus(forLifecycle, nowMs),
        });
      }

      return snapshots;
    },

    async presentSubscribePaywall(): Promise<boolean> {
      try {
        const { result } = await RevenueCatUI.presentPaywall();
        if (result !== PAYWALL_RESULT.NOT_PRESENTED && result !== PAYWALL_RESULT.ERROR) {
          reportPaywallEvent("um_paywall_shown", { placement: "subscribe" });
        }
        if (result === PAYWALL_RESULT.CANCELLED) {
          reportPaywallEvent("um_paywall_dismissed", { placement: "subscribe" });
        }
        if (result === PAYWALL_RESULT.PURCHASED) {
          void reportPurchaseConversionEvent();
        }
        return paywallResultIndicatesAccess(result);
      } catch (e) {
        console.error("Error presenting paywall", e);
        logException(e);
        return false;
      }
    },

    async presentPaywallIfNeeded(
      options: PaywallPresentationRequest
    ): Promise<boolean> {
      let offering: PurchasesOffering | null = null;
      const { placement, offeringId, soft, entitlement, onHardDismiss } = options;

      // 1. Check if the user is already entitled using cached/local customerInfo first
      try {
        if (entitlement) {
          const { customerInfo } = await Purchases.getCustomerInfo();
          if (entitlement in customerInfo.entitlements.active) {
            console.log("[paywall] User is already entitled to:", entitlement);
            return true;
          }
          // The cached snapshot can be stale (e.g. the account-creation promo
          // grant landing after configure's first fetch — the SDK caches
          // customerInfo for ~5 minutes). Presenting a paywall to an entitled
          // user is worse than one extra network round-trip, so force a fresh
          // fetch and re-check before committing to a presentation.
          console.log(
            "[paywall] Cached info shows no",
            entitlement,
            "entitlement — re-checking with a fresh fetch"
          );
          await Purchases.invalidateCustomerInfoCache();
          const { customerInfo: freshInfo } = await Purchases.getCustomerInfo();
          if (entitlement in freshInfo.entitlements.active) {
            console.log(
              "[paywall] User is entitled to",
              entitlement,
              "after cache refresh — skipping paywall"
            );
            return true;
          }
        }
      } catch (e) {
        console.error("[paywall] Error checking active entitlements from cache:", e);
        logException(e);
      }

      if (placement) {
        console.log(
          "[paywall] Syncing attributes and offerings if needed - presentPaywallIfNeeded"
        );
        try {
          const offerings = await Purchases.syncAttributesAndOfferingsIfNeeded();
          console.log(
            "[paywall] Offerings available - presentPaywallIfNeeded",
            offerings,
            offerings.current?.identifier
          );

          offering = await resolveOfferingForPlacementPath(
            placement,
            async (id) => {
              console.log("Getting current offering for placement", id);
              const offering = await Purchases.getCurrentOfferingForPlacement({
                placementIdentifier: id,
              });
              console.log("Offering availibile for placement", id, offering);
              return offering;
            }
          );
        } catch (e) {
          console.error("[paywall] Error syncing or resolving offerings:", e);
          logException(e);
          // If we fail due to network/offline, double check cache one last time
          try {
            const { customerInfo } = await Purchases.getCustomerInfo();
            if (entitlement && entitlement in customerInfo.entitlements.active) {
              return true;
            }
          } catch (innerErr) {
            logException(innerErr);
          }
          return false;
        }
        if (!offering) {
          // No offering at this placement = no paywall needed for this user.
          // Mirror the native RevenueCatUI behaviour where NOT_PRESENTED indicates access.
          console.log("No offering needed for this placement", placement);
          return true;
        }
      } else if (offeringId) {
        const offerings = await Purchases.getOfferings();
        const allOfferings = Array.isArray(offerings.all)
          ? offerings.all
          : Object.values(offerings.all ?? {});
        offering =
          allOfferings.find(
            (candidate) => candidate.identifier === offeringId
          ) ?? null;
        if (!offering) {
          console.log(
            "Requested offering unavailable, falling back to default offering",
            offeringId
          );
        }
      }

      const meta = offering?.metadata as OfferingMetadata | undefined;
      const isSoft = soft ?? Boolean(meta?.soft);
      const displayCloseButton = isSoft;
      const presentationConfiguration = {
        ios: isSoft
          ? IOS_PAYWALL_PRESENTATION_STYLE.SHEET
          : IOS_PAYWALL_PRESENTATION_STYLE.FULL_SCREEN,
        android: ANDROID_PAYWALL_PRESENTATION_STYLE.FULL_SCREEN,
      };

      let dismissCount = 0;
      for (;;) {
        const paywallVariables: PaywallVariables = { dismissCount };
        console.log("Presenting paywall if needed", offering);
        const { result } = await RevenueCatUI.presentPaywallIfNeeded({
          requiredEntitlementIdentifier: entitlement,
          offering: offering ?? undefined,
          displayCloseButton,
          presentationConfiguration,
          customVariables: {
            dismissCount: paywallVariables.dismissCount,
          },
        });
        console.log("Paywall presented", result);

        if (result !== PAYWALL_RESULT.NOT_PRESENTED && result !== PAYWALL_RESULT.ERROR) {
          reportPaywallEvent("um_paywall_shown", {
            placement,
            offeringId: offering?.identifier,
            dismissCount,
          });
        }
        if (result === PAYWALL_RESULT.CANCELLED) {
          reportPaywallEvent("um_paywall_dismissed", {
            placement,
            offeringId: offering?.identifier,
            dismissCount,
          });
        }
        if (result === PAYWALL_RESULT.PURCHASED) {
          void reportPurchaseConversionEvent();
        }
        if (paywallResultIndicatesAccess(result)) {
          return true;
        }
        if (result === PAYWALL_RESULT.ERROR) {
          try {
            const { customerInfo } = await Purchases.getCustomerInfo();
            if (entitlement in customerInfo.entitlements.active) {
              return true;
            }
          } catch (e) {
            logException(e);
          }
          return false;
        }
        if (result !== PAYWALL_RESULT.CANCELLED) {
          return false;
        }
        if (isSoft) {
          return false;
        }
        dismissCount += 1;
        if (onHardDismiss) {
          onHardDismiss();
          return false;
        }
      }
    },

    async manageSubscriptions(): Promise<void> {
      await RevenueCatUI.presentCustomerCenter();
    },

    async getEntitlements(): Promise<string[]> {
      try {
        const { customerInfo } = await Purchases.getCustomerInfo();
        return Object.keys(customerInfo.entitlements.active);
      } catch (e) {
        console.error("Error fetching entitlements", e);
        logException(e);
      }
      return [];
    },

    async invalidateCustomerInfoCache(): Promise<void> {
      try {
        await Purchases.invalidateCustomerInfoCache();
      } catch (e) {
        console.error("Error invalidating customer info cache", e);
        logException(e);
      }
    },

    async prefetchExperimentOffering(
      placement: string
    ): Promise<ResolvedExperimentOffering | null> {
      try {
        await Purchases.syncAttributesAndOfferingsIfNeeded();
        const offering = await resolveOfferingForPlacementPath(
          placement,
          async (id) => {
            const offering = await Purchases.getCurrentOfferingForPlacement({
              placementIdentifier: id,
            });
            return offering;
          }
        );
        if (!offering) return null;
        return {
          identifier: offering.identifier,
          metadata: (offering.metadata ?? {}) as OfferingMetadata,
        };
      } catch (e) {
        console.error("Error prefetching experiment offering", e);
        logException(e);
        return null;
      }
    },

    async setSubscriberAttributes(
      attrs: Partial<SubscriberAttributes>
    ): Promise<void> {
      try {
        await Purchases.setAttributes(
          attrs as Record<string, string | null>
        );
      } catch (e) {
        console.error("Error setting subscriber attributes", e);
        logException(e);
      }
    },

    dispose() {
      detachPaywallListeners();
    },
  };
}

async function configureNativePurchases(account: WithRef<Account>) {
  await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

  const platform = Capacitor.getPlatform();

  // Key selection is centralized in @um/contracts (revenueCatKeys.ts).
  const apiKey = getRevenueCatPublicApiKey({
    platform: platform === "ios" ? "ios" : "android",
    production: isProduction(),
  });
  console.log(
    `Using RevenueCat native API key ${apiKey.slice(0, apiKey.lastIndexOf("_") + 1)}…`
  );

  await Purchases.configure({
    apiKey: apiKey,
    appUserID: account.ref.id,
  });
  await Purchases.setDisplayName({ displayName: account.name });
  await Purchases.setEmail({ email: account.email });
  const { appInstanceId } = await FirebaseAnalytics.getAppInstanceId();
  if (appInstanceId) {
    await Purchases.setFirebaseAppInstanceID({
      firebaseAppInstanceID: appInstanceId,
    });
  }
  await Purchases.setAttributes({
    ...subscriberAttributesFromAccount(account),
  } as Record<string, string | null>);
  const { uid: appsflyerID } = await AppsFlyer.getAppsFlyerUID();
  await Purchases.setAppsflyerID({ appsflyerID });

  console.log("[paywall] Syncing attributes and offerings if needed");
  const offerings = await Purchases.syncAttributesAndOfferingsIfNeeded();
  console.log(
    "[paywall] Offerings available",
    offerings,
    offerings.current?.identifier,
    offerings.all.length
  );
  nativePurchasesConfigured = true;
}
